<template>
  <v-card
    v-if="childSchema"
    class="mb-4"
    flat
    tile
  >
    <v-card-text class="px-0">
      <v-row class="d-flex align-center">
        <v-col>
          <h2
            class="fs-22 fw-600"
            tabindex="0"
          >
            {{ subsidyProgram.name }}
          </h2>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <p class="c-light-black fs-16 fw-500 mb-0">
            {{ $bct(subsidyProgram.description) }}
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <template v-if="subsidy">
            <template v-if="!subsidySubmittedAt">
              <v-btn
                :block="$vuetify.display.smAndDown"
                :to="{ name: 'SubsidyContinue', params: { subsidyId: subsidyId } }"
                class="focus-very-visible"
                color="primary"
                data-cy="continue-button"
                variant="outlined"
                tile
              >
                <span>{{ $bct('Continue') }} {{ subsidyProgram.name }}</span>
              </v-btn>
            </template>
          </template>
          <template v-else>
            <v-btn
              @click="create"
              :block="$vuetify.display.smAndDown"
              :loading="processing"
              class="focus-very-visible"
              color="primary"
              data-cy="start-button"
              variant="outlined"
              tile
            >
              <span>{{ $bct('Start') }} {{ subsidyProgram.name }}</span>
            </v-btn>
          </template>
        </v-col>
      </v-row>

      <template v-if="subsidySubmittedAt">
        <v-divider class="my-4" />

        <v-row
          class="mb-2"
          dense
        >
          <v-col class="d-flex align-center">
            <router-link
              :to="{ name: 'SubsidyReview', params: { subsidyId: subsidyId } }"
              class="td-none"
            >
              <v-icon
                class="material-icons-outlined td-none"
                color="primary"
                icon="check_circle"
                size="24"
                start
              />

              <LongDateTime
                :date="subsidySubmittedAt"
                class="underlined"
                data-cy="app_submitted"
                prefix="Application submitted at"
              />
            </router-link>
          </v-col>
        </v-row>
      </template>

      <ChildSubsidyPreferenceList
        v-model:processing="processing"
        :child-data="childData"
        :child-schema="childSchema"
        :subsidy="subsidy"
        :subsidy-program="subsidyProgram"
      />
    </v-card-text>
  </v-card>
</template>

<script setup>
import LongDateTime from '@/shared/components/LongDateTime.vue';
import ChildSubsidyPreferenceList from '@/parent/components/subsidy/ChildSubsidyPreferenceList.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps({
  childData: {
    type: Object,
    default: null,
  },
  childSchema: {
    type: Object,
    default: null,
  },
  subsidy: {
    type: Object,
    default: null,
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
});

const { api } = useApi();
const eventBus = useEventBus();
const router = useRouter();
const route = useRoute();
const $bct = inject('translator');

const enrollments = ref([]);
const countEnrollmentsTowardPreferencesLimit = ref(0);
const subsidyId = ref(props.subsidy?.id);
const subsidySubmittedAt = ref(props.subsidy?.submitted_at);
const processing = ref(false);
const allowAddPrograms = ref(false);

onMounted(loadEnrollments);

function addProgramsAllowed() {
  if (
    props.subsidyProgram?.enable_iep_direct_placement &&
    props.subsidy?.child_application_data?.individualized_education_plan
  ) {
    return false;
  }
  if (props.subsidy?.preferences_unlocked_at) return true;
  if (!props.subsidyProgram.enable_automatic_preference_unlock) return false;

  // Handle when automatic preferences unlock is enabled
  if (enrollments.value.length === 0) return true;

  if (!enrollments.value.some((enrollment) => enrollment.prevents_automatic_preference_unlock)) {
    return true;
  }

  return false;
}

function create() {
  processing.value = true;
  api.subsidy.create(
    { child_id: props.childData.id, subsidy_program_id: props.subsidyProgram.id },
    (resp) => {
      router.push({ name: 'SubsidyContinue', params: { subsidyId: resp.data.id } });
    },
    (err) => {
      eventBus.chime(err.response.data.errors[0]);
    },
  );
}

async function loadEnrollments() {
  const resp = await api.parent.enrollment.index({ child_id: props.childData.id });
  if (resp?.data) {
    enrollments.value = resp.data;
    const filteredEnrollments = enrollments.value
      .filter((enrollment) => enrollment.subsidy_program_id === props.subsidyProgram.id)
      .sort((a, b) => a.preference_order - b.preference_order);

    enrollments.value = filteredEnrollments;
    allowAddPrograms.value = addProgramsAllowed();
  }

  const countResponse = await api.parent.enrollment.index({
    child_id: route.params.childId,
    count: true,
    subsidy_id: props.subsidyId,
  });

  if (countResponse?.data) {
    countEnrollmentsTowardPreferencesLimit.value = countResponse.data.count;
  }
}
</script>
